import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core'
import { NavLink, withRouter } from 'react-router-dom'
import { HOME_PATH } from '../../../configs'
import { getRoleLabel } from '../helpers/organisationFields'
import {
  USAGE_METRIC,
  USAGE_METRIC_META,
  getUsageRatio,
  isUnlimited,
} from '../../../constants/usageLimits'
import { SUPERVISION_MODE_LABEL } from '../../../constants/supervisionMode'
import { ROLE } from '../../../security'
import OrganisationRowActions from './OrganisationRowActions'
import AssistantStatusModal from './AssistantStatusModal'
import { AssistantColorSwitch } from '../../../uiKit/Switch'
import {
  isBotPackageCarePlanEnabled,
  isBotPackageSupervisionEnabled,
} from '../api/usageLimits'
import { getCachedBotPackageAssignment } from '../helpers/orgPackages'

/**
 * Organisations list.
 *
 * Every column here is backed by a real field on the bot payload or by the
 * signed-in admin's own access record. Nothing is derived from a placeholder:
 * the previous "Type: QCS / Foundation" column was computed as `id % 3 === 0`,
 * which reads as fact to an operator and is not.
 *
 * DESIGN.md compliance notes:
 *  - §04 rule 7 / §03: striped with {colors.row-default} / {colors.row-alt};
 *    no card wrapper, no shadow. Hairline rules top and bottom anchor the table
 *    against the page, since the page canvas and the default stripe are only
 *    one step apart.
 *  - §02 "Colour as Meaning, Not Decoration": the monogram is a single neutral
 *    treatment. It previously hashed the organisation name to one of five hues,
 *    which encoded nothing and read as noise.
 *  - §02 "Status Is Never Color Alone": assistant state always ships its label.
 */

const styles = () => ({
  // Elevated card surface rather than a bare striped block. DESIGN.md §13 keeps
  // the table flat, but the in-session request to lift it out of "wireframe" is
  // higher in the source-of-truth order (§26). Still built only from the token
  // set — surface, border, rounded-lg, and shadows.card.
  wrap: {
    width: '100%',
    background: 'var(--careco-surface)',
    border: '1px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-lg, 20px)',
    boxShadow: 'var(--careco-shadow-card, 0 2px 10px 2px rgba(110,110,110,0.06))',
    overflow: 'hidden',
  },
  scroll: {
    overflowX: 'auto',
    width: '100%',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'var(--careco-font)',
    tableLayout: 'fixed',
    // Floor width so narrow viewports scroll horizontally instead of crushing
    // headers (e.g. "Your access" + sort carets).
    minWidth: 760,
  },
  tableMetered: {
    minWidth: 1320,
  },
  th: {
    textAlign: 'left',
    padding: '18px 20px',
    borderBottom: '1px solid var(--careco-border)',
    color: 'var(--careco-text-secondary)',
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    // Quiet white header on the card; sentence-case labels and the rule below
    // carry the separation, matching the reference table.
    background: 'var(--careco-surface)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  thSortable: {
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': { color: 'var(--careco-primary)' },
    // Sortable columns advertise themselves: the carets sit muted until hover,
    // then take the interaction colour so the whole header reads as clickable.
    '&:hover $sortIcon': { color: 'var(--careco-primary)' },
    '&:focus-visible': {
      outline: '2px solid var(--careco-primary)',
      outlineOffset: -2,
    },
  },
  thInner: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  // A crisp up/down double-chevron icon, vertically centred next to the label.
  // Muted while unsorted (so the column still reads as sortable); once sorted,
  // the whole icon takes the interaction colour and the inactive direction is
  // dimmed so the active one stands out.
  sortIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    // Explicit margin for the label-to-icon spacing (robust regardless of how
    // the flex row lays out its label text node).
    marginLeft: 6,
    color: 'var(--careco-text-muted)',
    transition: 'color 120ms ease',
  },
  sortIconActive: {
    color: 'var(--careco-primary)',
  },
  sortSvg: {
    display: 'block',
  },
  chevronDim: {
    opacity: 0.35,
  },
  // Non-metered (CareBrain) list. Same spacing shape as the QCS table: a wide
  // name column, a mid-width access column, then a narrow trailing column that
  // ends up near the right edge. Everything stays left-aligned in its cell,
  // matching the QCS "Limits" column.
  colNameCare: { width: '40%' },
  colAccessCare: { width: '24%' },
  colAiModelCare: { width: '18%' },
  colActionsCare: { width: '18%' },

  // Metered (QCS) list: name, access, renewal, tier, supervision, meter,
  // status label, (admin) limits, activation toggle.
  colNameMetered: { width: '16%' },
  // Pixel width — under table-layout:fixed, % + minWidth still crushes this
  // header ("Your access" + sort carets + cell padding).
  colAccess: { width: 152 },
  colRenewal: { width: '13%' },
  colTier: { width: '9%' },
  colSupervisionMode: { width: '10%' },
  colUsage: { width: '15%' },
  colStatus: { width: '9%' },
  colActions: { width: '7%' },
  colAssistant: { width: 144, whiteSpace: 'normal' },
  // Toggle column — switch is the only content.
  assistantCell: {
    display: 'flex',
    alignItems: 'center',
  },
  // Status: dot + word (§02 "Status Is Never Color Alone").
  statusCell: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  statusDot: {
    width: 8,
    height: 8,
    flexShrink: 0,
    borderRadius: '50%',
    marginRight: 2,
  },
  statusDotOn: { background: 'var(--careco-success)' },
  statusDotOff: { background: 'var(--careco-text-muted)' },
  statusLabel: {
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  statusLabelOn: { color: 'var(--careco-success)' },
  statusLabelOff: { color: 'var(--careco-text-secondary)' },

  // Gentle zebra striping (reference table): white base rows with a faint tint
  // on every second row, hover lifting the whole row to primary-light. The tint
  // carries the separation, so no per-row hairline is needed.
  row: {
    background: 'var(--careco-surface)',
    // The whole row opens the organisation, so it reads as clickable.
    cursor: 'pointer',
    transition: 'background 120ms ease, opacity 120ms ease',
    '&:nth-child(even)': { background: 'var(--careco-row-default)' },
    '&:hover': { background: 'var(--careco-primary-light)' },
    // Divider track washes out on primary-light hover — keep the bar trough white.
    '&:hover $track': { background: 'var(--careco-surface)' },
  },
  // Deactivated org — whole row fades so inactive is obvious while scanning.
  rowInactive: {
    opacity: 0.55,
    '&:hover': { background: 'var(--careco-row-default)' },
  },
  td: {
    padding: '0 20px',
    height: 72,
    color: 'var(--careco-text-secondary)',
    fontSize: 14,
    verticalAlign: 'middle',
  },
  nameCell: {
    padding: 0,
    height: 72,
    verticalAlign: 'middle',
  },
  // The whole name cell is the hit target, not just the text.
  nameLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '0 20px',
    height: 72,
    textDecoration: 'none',
    color: 'var(--careco-text-primary)',
    '&:focus-visible': {
      outline: '2px solid var(--careco-primary)',
      outlineOffset: -2,
    },
  },
  // Round avatar-style monogram, matching the reference identity cell.
  monogram: {
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: 'var(--careco-rounded-pill, 999px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--careco-primary-light)',
    color: 'var(--careco-primary)',
    fontSize: 13,
    fontWeight: 700,
  },
  name: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  access: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
  },
  accessNone: {
    fontSize: 14,
    fontWeight: 400,
    color: 'var(--careco-text-muted)',
  },
  // §11 status-pill-positive, used verbatim.
  pillPositive: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '5px 14px',
    borderRadius: 'var(--careco-rounded-pill)',
    fontSize: 13.5,
    fontWeight: 600,
    background: 'var(--careco-success-light)',
    color: 'var(--careco-success)',
    whiteSpace: 'nowrap',
  },
  // Not a failure, so no red and no filled pill — §04 rule 4.
  statusNeutral: {
    fontSize: 13.5,
    fontWeight: 600,
    color: 'var(--careco-status-inactive)',
    whiteSpace: 'nowrap',
  },
  // Account-tier pill. Three tokens already in the palette, reused rather than
  // invented: amber for Gold, the brand primary for Platinum (the one tier
  // that gets the "highlighted" treatment), and a plain neutral pill for Basic
  // — a tier is a fact about the account, not a status, so Basic reads as
  // "plain", never as a warning.
  tierPill: {
    display: 'inline-block',
    boxSizing: 'border-box',
    maxWidth: 95,
    padding: '4px 12px',
    borderRadius: 'var(--careco-rounded-pill)',
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
  },
  tierGold: {
    background: 'var(--careco-status-inactive-light)',
    color: 'var(--careco-status-inactive)',
  },
  tierPlatinum: {
    background: 'var(--careco-primary-light)',
    color: 'var(--careco-primary)',
  },
  tierBasic: {
    background: 'var(--careco-divider)',
    color: 'var(--careco-text-secondary)',
  },
  // Supervision mode. Plain text, not a pill — same treatment as "Your access"
  // (§11 identity text, not a status), since Dynamic vs Default is a fact
  // about how the account is configured, not a state to flag.
  supervisionModeText: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    whiteSpace: 'nowrap',
  },
  // The renewal countdown, stacked: coloured days-left on top, the exact
  // renewal date beneath it.
  renewalCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  renewalText: {
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  // Plenty of runway, worth a heads-up, and due now — the same semantics as
  // the usage bar's default/warn/spent colours, reused rather than invented.
  renewalOk: { color: 'var(--careco-success)' },
  renewalWarn: { color: 'var(--careco-status-inactive)' },
  renewalUrgent: { color: 'var(--careco-error)' },
  renewalDateText: {
    fontSize: 12,
    color: 'var(--careco-text-muted)',
    whiteSpace: 'nowrap',
  },
  updated: {
    fontSize: 14,
    color: 'var(--careco-text-secondary)',
    fontVariantNumeric: 'tabular-nums',
  },
  muted: { color: 'var(--careco-text-muted)' },

  usageCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    padding: '6px 0',
    maxWidth: 180,
  },
  // Bar on the left, percentage on the right — the reference layout.
  usageTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  percent: {
    flexShrink: 0,
    fontSize: 13,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    color: 'var(--careco-text-primary)',
    whiteSpace: 'nowrap',
  },
  percentSpent: { color: 'var(--careco-error)' },
  usageSub: {
    fontSize: 12,
    fontVariantNumeric: 'tabular-nums',
    color: 'var(--careco-text-muted)',
    whiteSpace: 'nowrap',
  },
  usageValue: {
    fontSize: 13,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    color: 'var(--careco-text-primary)',
    whiteSpace: 'nowrap',
  },
  // `display: block` is load-bearing. These are spans inside a table cell, and
  // an inline box ignores height and width — the bars rendered as empty grey
  // tracks whatever the usage was, so every organisation looked identical.
  track: {
    display: 'block',
    flex: '1 1 auto',
    minWidth: 0,
    height: 6,
    borderRadius: 'var(--careco-rounded-pill, 999px)',
    background: 'var(--careco-divider)',
    overflow: 'hidden',
  },
  fill: {
    display: 'block',
    height: '100%',
    borderRadius: 'inherit',
    background: 'var(--careco-primary)',
  },
  fillWarn: { background: 'var(--careco-status-inactive)' },
  fillSpent: { background: 'var(--careco-error)' },
  manage: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'var(--careco-font)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--careco-primary)',
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
  },
  empty: {
    padding: '56px 20px',
    textAlign: 'center',
    background: 'var(--careco-surface)',
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    marginBottom: 4,
  },
  emptyBody: {
    fontSize: 14,
    color: 'var(--careco-text-muted)',
  },
  '@media (max-width: 900px)': {
    th: {
      padding: '14px 12px',
    },
    td: {
      padding: '14px 12px',
    },
  },
})

// Only care-plan audits still renders as a usage meter — Supervision shows the
// account's supervision mode instead (see renderSupervisionMode).
const METERED_COLUMNS = [USAGE_METRIC.CARE_PLAN_AUDITS]

const TIER_LABEL = {
  GOLD: 'Gold',
  PLATINUM: 'Platinum',
  BASIC: 'Basic',
}

const TIER_CLASS = {
  GOLD: 'tierGold',
  PLATINUM: 'tierPlatinum',
  BASIC: 'tierBasic',
}

// packageTiers.tierLevelFor returns title-case Basic/Gold/Platinum.
const PACKAGE_TIER_CLASS = {
  Basic: 'tierBasic',
  Gold: 'tierGold',
  Platinum: 'tierPlatinum',
}

const RENEWAL_URGENCY_CLASS = {
  ok: 'renewalOk',
  warn: 'renewalWarn',
  urgent: 'renewalUrgent',
}

const OrganisationsTable = props => {
  const {
    classes,
    rows,
    sortBy,
    sortDir,
    onSort,
    onOpen,
    searchTerm,
    hasFilters,
    // Metered columns are opt-in: the regular organisations list has no
    // allowances, so passing nothing leaves the table exactly as it was.
    usageByBotId,
    onManageLimits,
    onToggleActivation,
    togglingBotId,
    onCloned,
    history,
  } = props

  const canManageRow = row => row.role === ROLE.ADMIN
  // Same rule as the Manage button — keeps the column and its cells in sync.
  const canManageLimits = row => canManageRow(row) && Boolean(row.botActivated)
  const showLimitsColumn = Boolean(usageByBotId) && (rows || []).some(canManageLimits)

  // Open the organisation the same way the name link does — set it active, then
  // route to it — but from a click anywhere on the row.
  const openRow = row => {
    onOpen(row.bot)
    history.push(`${HOME_PATH}/bot/${row.id}/${row.landingTab}`)
  }

  // Toggle never flips on click — both directions reset renewal, so confirm first.
  const [pendingRow, setPendingRow] = useState(null)
  const requestActivationToggle = row => setPendingRow(row)
  const closeActivationToggle = () => setPendingRow(null)
  const confirmActivationToggle = () => {
    if (pendingRow && onToggleActivation) {
      onToggleActivation(pendingRow, !Boolean(pendingRow.botActivated))
    }
    setPendingRow(null)
  }

  const isMetered = Boolean(usageByBotId)
  const showToggleColumn =
    isMetered && Boolean(onToggleActivation) && (rows || []).some(canManageRow)
  // Metered (QCS): name, access, renewal, tier, supervision, status (6),
  // plus meters, [limits], [toggle]. Non-metered (CareBrain): 4 columns.
  const columnCount = isMetered
    ? 6 + METERED_COLUMNS.length + (showLimitsColumn ? 1 : 0) + (showToggleColumn ? 1 : 0)
    : 4

  const emptyDash = (
    <span className={`${classes.updated} ${classes.muted}`}>—</span>
  )

  const getRowAssignment = row =>
    row.bot?.packageDetails || getCachedBotPackageAssignment(row.id) || null

  // Status column: label only — always mirrors botActivated.
  const renderActivationStatus = row => {
    const active = Boolean(row.botActivated)
    return (
      <span className={classes.statusCell}>
        <span
          className={`${classes.statusDot} ${active ? classes.statusDotOn : classes.statusDotOff}`}
          aria-hidden="true"
        />
        <span
          className={`${classes.statusLabel} ${
            active ? classes.statusLabelOn : classes.statusLabelOff
          }`}>
          {active ? 'Active' : 'Inactive'}
        </span>
      </span>
    )
  }

  // Trailing toggle column — opens confirm modal; API runs on confirm.
  const renderActivationToggle = row => {
    if (!canManageRow(row) || !onToggleActivation) return null
    const active = Boolean(row.botActivated)
    const busy = togglingBotId != null && Number(togglingBotId) === Number(row.id)
    return (
      <span className={classes.assistantCell}>
        <AssistantColorSwitch
          checked={active}
          disabled={busy}
          ariaLabel={active ? 'Deactivate organisation' : 'Activate organisation'}
          onChange={e => {
            e.stopPropagation()
            requestActivationToggle(row)
          }}
        />
      </span>
    )
  }

  const renderTier = row => {
    // An assigned package is the org's tier; style from its feature count when known.
    if (row.packageName) {
      const tierClassKey = PACKAGE_TIER_CLASS[row.packageTier]
      const tierClass = tierClassKey ? classes[tierClassKey] : classes.tierPlatinum
      return (
        <span className={`${classes.tierPill} ${tierClass}`} title={row.packageName}>
          {row.packageName}
        </span>
      )
    }
    if (!row.tier || !TIER_LABEL[row.tier]) {
      return <span className={`${classes.updated} ${classes.muted}`}>—</span>
    }
    return (
      <span className={`${classes.tierPill} ${classes[TIER_CLASS[row.tier]]}`}>
        {TIER_LABEL[row.tier]}
      </span>
    )
  }

  const renderSupervisionMode = row => {
    const assignment = getRowAssignment(row)
    if (assignment && !isBotPackageSupervisionEnabled(assignment)) {
      return emptyDash
    }
    if (!row.supervisionMode || !SUPERVISION_MODE_LABEL[row.supervisionMode]) {
      return emptyDash
    }
    return <span className={classes.supervisionModeText}>{SUPERVISION_MODE_LABEL[row.supervisionMode]}</span>
  }

  // Plain text — model name comes from bot.aiModel or odinConfigs.aiModel.modelName.
  const renderAiModel = row => {
    if (!row.aiModel) {
      return emptyDash
    }
    return <span className={classes.supervisionModeText}>{row.aiModel}</span>
  }

  // The renewal cycle: how many days remain (coloured by urgency) and the
  // date it renews on. Deactivated orgs and rows without a renewal date show
  // an em dash — nothing to count down.
  const renderRenewalCycle = row => {
    if (!row.botActivated || !row.renewalInfo) {
      return emptyDash
    }
    return (
      <span className={classes.renewalCell}>
        <span
          className={`${classes.renewalText} ${
            classes[RENEWAL_URGENCY_CLASS[row.renewalInfo.urgency]]
          }`}>
          {row.renewalInfo.daysLeftLabel}
        </span>
        <span className={classes.renewalDateText}>{row.renewalInfo.renewsOnLabel}</span>
      </span>
    )
  }

  const renderUsage = (row, metric) => {
    if (metric === USAGE_METRIC.CARE_PLAN_AUDITS) {
      const assignment = getRowAssignment(row)
      if (assignment && !isBotPackageCarePlanEnabled(assignment)) {
        return emptyDash
      }
    }
    const entry = usageByBotId?.[row.id]?.metrics?.[metric]

    // An unread counter shows an em dash, never a zero. "0 of 5" is a claim
    // about the customer's remaining allowance; "—" is the honest answer.
    if (!entry || typeof entry.used !== 'number') {
      return emptyDash
    }

    const unlimited = isUnlimited(entry.limit)
    const ratio = getUsageRatio(entry.used, entry.limit)
    const spent = !unlimited && entry.used >= entry.limit
    const warn = !spent && ratio >= 0.8

    if (unlimited) {
      return <span className={classes.usageValue}>{entry.used} used · no limit</span>
    }

    // Prefer bot/list carePlanAuditUsagePercentage for the care-plan audits
    // bar + label; fall back to used/limit when the field is missing.
    const pctFromApi =
      metric === USAGE_METRIC.CARE_PLAN_AUDITS &&
      typeof row.carePlanAuditUsagePercentage === 'number'
        ? Math.round(row.carePlanAuditUsagePercentage)
        : null
    const pct = Math.min(100, Math.max(0, pctFromApi ?? Math.round(ratio * 100)))

    return (
      <span className={classes.usageCell}>
        <span className={classes.usageTop}>
          <span className={classes.track}>
            <span
              className={`${classes.fill} ${warn ? classes.fillWarn : ''} ${
                spent ? classes.fillSpent : ''
              }`}
              style={{ width: `${pct}%` }}
            />
          </span>
          <span className={`${classes.percent} ${spent ? classes.percentSpent : ''}`}>{pct}%</span>
        </span>
        <span className={classes.usageSub}>
          {entry.used} of {entry.limit}
        </span>
      </span>
    )
  }

  const header = (label, key, widthClass, reactKey) => {
    const isSorted = sortBy === key
    const ascActive = isSorted && sortDir === 'asc'
    const descActive = isSorted && sortDir === 'desc'
    return (
      <th
        key={reactKey}
        className={`${classes.th} ${classes.thSortable} ${widthClass}`}
        scope="col"
        tabIndex={0}
        aria-sort={isSorted ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        onClick={() => onSort(key)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onSort(key)
          }
        }}>
        <span className={classes.thInner}>
          <span>{label}</span>
          <span
            className={`${classes.sortIcon} ${isSorted ? classes.sortIconActive : ''}`}
            aria-hidden="true">
            <svg className={classes.sortSvg} width="11" height="14" viewBox="0 0 11 14" fill="none">
              <path
                d="M2.5 5 L5.5 2 L8.5 5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={descActive ? classes.chevronDim : ''}
              />
              <path
                d="M2.5 9 L5.5 12 L8.5 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={ascActive ? classes.chevronDim : ''}
              />
            </svg>
          </span>
        </span>
      </th>
    )
  }

  return (
    <div className={classes.wrap}>
      <div className={classes.scroll}>
        <table className={`${classes.table} ${isMetered ? classes.tableMetered : ''}`}>
          <thead>
            <tr>
              {header(
                isMetered ? 'Organisation' : 'CareBrain organisation',
                'name',
                isMetered ? classes.colNameMetered : classes.colNameCare,
              )}
              {header(
                'Your access',
                'access',
                isMetered ? classes.colAccess : classes.colAccessCare,
              )}
              {isMetered && (
                <th className={`${classes.th} ${classes.colRenewal}`} scope="col">
                  Renewal cycle
                </th>
              )}
              {isMetered && header('Tier', 'packageName', classes.colTier)}
              {isMetered && (
                <th className={`${classes.th} ${classes.colSupervisionMode}`} scope="col">
                  Supervision
                </th>
              )}
              {!isMetered && (
                <th className={`${classes.th} ${classes.colAiModelCare}`} scope="col">
                  AI Model
                </th>
              )}
              {isMetered &&
                METERED_COLUMNS.map(metric =>
                  header(USAGE_METRIC_META[metric].label, metric, classes.colUsage, metric),
                )}
              {!isMetered && (
                <th className={`${classes.th} ${classes.colActionsCare}`} scope="col">
                  Actions
                </th>
              )}
              {isMetered && (
                <th className={`${classes.th} ${classes.colStatus}`} scope="col">
                  Status
                </th>
              )}
              {showLimitsColumn && (
                <th className={`${classes.th} ${classes.colActions}`} scope="col">
                  Limits
                </th>
              )}
              {showToggleColumn && (
                <th className={`${classes.th} ${classes.colAssistant}`} scope="col">
                  Deactivate
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr
                className={`${classes.row} ${
                  isMetered && row.botActivated === false ? classes.rowInactive : ''
                }`}
                key={row.id}
                onClick={() => openRow(row)}>
                <td className={classes.nameCell}>
                  <NavLink
                    className={classes.nameLink}
                    to={`${HOME_PATH}/bot/${row.id}/${row.landingTab}`}
                    onClick={e => {
                      // The row handler already navigates — don't let the link
                      // fire it a second time.
                      e.stopPropagation()
                      onOpen(row.bot)
                    }}>
                    <span className={classes.monogram} aria-hidden="true">
                      {row.initials}
                    </span>
                    <span className={classes.name} title={row.name}>
                      {row.name}
                    </span>
                  </NavLink>
                </td>
                <td className={classes.td}>
                  {row.role ? (
                    <span className={classes.access}>{getRoleLabel(row.role)}</span>
                  ) : (
                    <span className={classes.accessNone}>—</span>
                  )}
                </td>
                {isMetered && <td className={classes.td}>{renderRenewalCycle(row)}</td>}
                {isMetered && <td className={classes.td}>{renderTier(row)}</td>}
                {isMetered && <td className={classes.td}>{renderSupervisionMode(row)}</td>}
                {!isMetered && <td className={classes.td}>{renderAiModel(row)}</td>}
                {isMetered &&
                  METERED_COLUMNS.map(metric => (
                    <td className={classes.td} key={metric}>
                      {renderUsage(row, metric)}
                    </td>
                  ))}
                {!isMetered && (
                  <td className={classes.td} onClick={e => e.stopPropagation()}>
                    <OrganisationRowActions row={row} onCloned={onCloned} />
                  </td>
                )}
                {isMetered && <td className={classes.td}>{renderActivationStatus(row)}</td>}
                {showLimitsColumn && (
                  <td className={classes.td}>
                    {canManageLimits(row) ? (
                      <button
                        type="button"
                        className={classes.manage}
                        onClick={e => {
                          // Managing limits is its own action — not "open the org".
                          e.stopPropagation()
                          onManageLimits(row)
                        }}>
                        Manage
                      </button>
                    ) : null}
                  </td>
                )}
                {showToggleColumn && (
                  <td className={classes.td} onClick={e => e.stopPropagation()}>
                    {renderActivationToggle(row)}
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td className={classes.empty} colSpan={columnCount}>
                  <div className={classes.emptyTitle}>No organisations found</div>
                  <div className={classes.emptyBody}>
                    {searchTerm
                      ? `Nothing matches “${searchTerm}”.`
                      : hasFilters
                        ? 'No organisation matches the current filters.'
                        : 'Create an organisation to get started.'}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <AssistantStatusModal
        open={Boolean(pendingRow)}
        activating={Boolean(pendingRow && !pendingRow.botActivated)}
        onCancel={closeActivationToggle}
        onConfirm={confirmActivationToggle}
      />
    </div>
  )
}

OrganisationsTable.propTypes = {
  classes: PropTypes.object,
  rows: PropTypes.array,
  sortBy: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func,
  onOpen: PropTypes.func,
  searchTerm: PropTypes.string,
  hasFilters: PropTypes.bool,
  usageByBotId: PropTypes.object,
  onManageLimits: PropTypes.func,
  onToggleActivation: PropTypes.func,
  togglingBotId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCloned: PropTypes.func,
  history: PropTypes.object,
}

export default withRouter(withStyles(styles)(OrganisationsTable))
